import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { AssetBalance } from '@olv/ui-shared/utils/asset-balance.utils';
import { CurrencyBalances } from '@olv/ui-shared/models/wallet.model';
import { SplitPartsPipe } from '@olv/ui-shared/pipes/split-parts.pipe';
import { CurrencyIconComponent } from '../currency-icon/currency-icon.component';
import { RiskDistributionTooltipComponent } from '../risk-distribution-tooltip/risk-distribution-tooltip.component';

@Component({
  selector: 'app-asset-card',
  standalone: true,
  imports: [MatIconModule, SplitPartsPipe, CurrencyIconComponent, RiskDistributionTooltipComponent],
  templateUrl: './asset-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./asset-card.component.scss'],
})
export class AssetCardComponent implements OnDestroy, AfterViewChecked {
  @Input({ required: true }) asset!: AssetBalance;
  @ViewChild('riskPopup') riskPopup?: ElementRef<HTMLElement>;

  expanded = false;
  hoveredEntry: CurrencyBalances | null = null;
  popupTop = 0;
  popupLeft = 0;
  private popupNeedsClamp = false;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly popupWidth = 340;
  private readonly popupGap = 6;
  private readonly viewportPadding = 16;

  get isExpandable(): boolean {
    return this.asset.entries.length > 1;
  }

  toggle(): void {
    if (!this.isExpandable) return;
    this.expanded = !this.expanded;
    this.hoveredEntry = null;
  }

  showRisk(entry: CurrencyBalances, event: Event): void {
    if (!entry.riskDistribution?.length) return;
    this.clearHideTimeout();

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const width = Math.min(this.popupWidth, window.innerWidth - this.viewportPadding * 2);
    this.popupLeft = Math.max(this.viewportPadding, Math.min(rect.left, window.innerWidth - this.viewportPadding - width));
    this.popupTop = rect.bottom + this.popupGap;
    this.hoveredEntry = entry;
    this.popupNeedsClamp = true;
  }

  ngAfterViewChecked(): void {
    if (!this.popupNeedsClamp || !this.riskPopup) return;
    this.popupNeedsClamp = false;

    const popup = this.riskPopup.nativeElement;
    const maxTop = window.innerHeight - this.viewportPadding - popup.offsetHeight;
    const top = this.popupTop > maxTop ? Math.max(this.viewportPadding, maxTop) : this.popupTop;
    popup.style.top = `${top}px`;
  }

  keepRiskOpen(): void {
    this.clearHideTimeout();
  }

  hideRisk(): void {
    this.clearHideTimeout();
    this.hideTimeout = setTimeout(() => {
      this.hoveredEntry = null;
      this.hideTimeout = null;
    }, 120);
  }

  ngOnDestroy(): void {
    this.clearHideTimeout();
  }

  private clearHideTimeout(): void {
    if (!this.hideTimeout) return;
    clearTimeout(this.hideTimeout);
    this.hideTimeout = null;
  }
}
